import { useState } from "react";
import { useLocation } from "wouter";
import { useAdminAuth } from "@/lib/adminAuth";
import AgentsTab from "@/components/admin/AgentsTab";
import BlunaTab from "@/components/admin/BlunaTab";
import CarouselTab from "@/components/admin/CarouselTab";
import OrdersTab from "@/components/admin/OrdersTab";
import CouponsTab from "@/components/admin/CouponsTab";
import { Seo } from "@/lib/seo";

type Tab = "agents" | "bluna" | "carousel" | "orders" | "coupons";

const TAB_LABELS: Record<Tab, string> = {
  agents: "Agents",
  bluna: "Bluna Prompt",
  carousel: "Carousel",
  orders: "Orders",
  coupons: "Coupons",
};

export default function Admin() {
  const { logout } = useAdminAuth();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState<Tab>("agents");

  const handleLogout = () => {
    logout();
    setLocation("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Seo
        title="Admin | AgentsToGo"
        description="AgentsToGo admin dashboard."
        path="/admin"
        noindex
      />
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <span className="text-white text-lg font-light tracking-tight">
              agents<span className="font-bold">to</span>go<span className="text-blue-400 font-bold">.</span>
            </span>
            <span className="text-white/30 text-sm font-medium">Admin Portal</span>
          </div>
          <button
            onClick={handleLogout}
            className="text-white/50 hover:text-white/80 text-sm transition"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex gap-1 mb-6 sm:mb-8 bg-white/5 rounded-xl p-1 w-fit max-w-full flex-wrap">
          {(["agents", "bluna", "carousel", "orders", "coupons"] as Tab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 sm:px-5 py-2 rounded-lg text-sm font-medium transition capitalize ${
                activeTab === tab
                  ? "bg-purple-600 text-white shadow"
                  : "text-white/50 hover:text-white"
              }`}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        {activeTab === "agents" && <AgentsTab />}
        {activeTab === "bluna" && <BlunaTab />}
        {activeTab === "carousel" && <CarouselTab />}
        {activeTab === "orders" && <OrdersTab />}
        {activeTab === "coupons" && <CouponsTab />}
      </div>
    </div>
  );
}
